<template>
  <div class="app">
    <NavSidebar v-if="isAuthenticated" />
    <div
      v-if="isAuthenticated"
      class="app-main-column"
      :class="{ 'sidebar-collapsed': sidebarCollapsed }"
    >
      <AppTopBar />
      <main class="main-content has-internal-footer">
        <ComplianceSpokeNav v-if="showComplianceSpokeNav" />
        <PortfolioSpokeNav v-else-if="showPortfolioSpokeNav" />
        <BuildSpokeNav v-else-if="showBuildSpokeNav" />
        <LearningSpokeNav v-else-if="showLearningSpokeNav" />
        <router-view :key="$route.fullPath"></router-view>
      </main>
    </div>
    <main v-else class="main-content no-sidebar">
      <router-view :key="$route.fullPath"></router-view>
    </main>
    <InternalFooter v-if="isAuthenticated" />
  </div>
</template>

<script>
import NavSidebar from '@/components/NavSidebar.vue'
import AppTopBar from '@/components/AppTopBar.vue'
import InternalFooter from '@/components/InternalFooter.vue'
import ComplianceSpokeNav from '@/components/compliance/ComplianceSpokeNav.vue'
import PortfolioSpokeNav from '@/components/portfolio/PortfolioSpokeNav.vue'
import BuildSpokeNav from '@/components/build-hub/BuildSpokeNav.vue'
import LearningSpokeNav from '@/components/learning/LearningSpokeNav.vue'
import auth from '@/services/auth'
import whitelabelBranding from '@/services/whitelabelBrandingService'
import { ACCESS_DENIED_STORAGE_KEY } from '@/router/routeAccess'

/** Routes that live inside the Compliance spoke (local nav, not hub). */
const COMPLIANCE_SPOKE_PREFIXES = [
  '/compliance-health',
  '/coi/integrations',
  '/compliance-report',
  '/compliance-responsiveness',
  '/active-locations-report',
  '/carrier-intelligence',
  '/carrier-analytics',
  '/project68',
]

/** Routes that live inside the Build spoke (local nav under Build landmark). */
const BUILD_SPOKE_PREFIXES = [
  '/build-management',
  '/construction-dashboard',
  '/construction-wip',
  '/development-budgets',
  '/house-pricing',
  '/house-budget',
  '/loan-draws',
  '/invoice-upload',
  '/gap-resolution',
  '/permit-completion',
  '/buyer-portal',
  '/master-data-audit',
  '/knowledge-graph',
  '/duplicate-detection',
  '/wealth/equity-screens',
]

export default {
  name: 'App',
  components: {
    NavSidebar,
    AppTopBar,
    InternalFooter,
    ComplianceSpokeNav,
    PortfolioSpokeNav,
    BuildSpokeNav,
    LearningSpokeNav,
  },

  data() {
    return {
      sidebarCollapsed: false,
    };
  },

  async mounted() {
    try {
      await whitelabelBranding.initialize();
      console.log('✅ Whitelabel branding initialized');
    } catch (error) {
      console.error('⚠️ Failed to initialize branding:', error);
    }
    window.addEventListener('adi-sidebar-collapsed', this.onSidebarCollapsed);
    const sidebar = document.querySelector('.sidebar');
    if (sidebar) {
      this.sidebarCollapsed = sidebar.classList.contains('collapsed');
    }
    this.showPendingAccessDeniedToast();
  },

  beforeUnmount() {
    window.removeEventListener('adi-sidebar-collapsed', this.onSidebarCollapsed);
  },

  watch: {
    $route() {
      this.showPendingAccessDeniedToast();
    },
  },

  computed: {
    isAuthenticated() {
      this.$route.path;
      if (this.$route.path.includes('/secure-upload/')) {
        return false;
      }
      return auth.isAuthenticated();
    },
    showComplianceSpokeNav() {
      if (!this.isAuthenticated) return false;
      const path = this.$route.path;
      if (COMPLIANCE_SPOKE_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) {
        return true;
      }
      return path.includes('/franchise-data') || path.includes('/match-hygiene');
    },
    showPortfolioSpokeNav() {
      if (!this.isAuthenticated) return false;
      const path = this.$route.path;
      if (path === '/clients' || path === '/clients/') return true;
      if (path === '/clients/all' || path.startsWith('/clients/all/')) return true;
      if (path === '/projects' || path === '/projects/') return true;
      return false;
    },
    showBuildSpokeNav() {
      if (!this.isAuthenticated) return false;
      const path = this.$route.path;
      return BUILD_SPOKE_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
    },
    showLearningSpokeNav() {
      if (!this.isAuthenticated) return false;
      const path = this.$route.path;
      return path === '/learning' || path.startsWith('/learning/');
    },
  },
  methods: {
    onSidebarCollapsed(event) {
      this.sidebarCollapsed = Boolean(event.detail?.collapsed);
    },
    showPendingAccessDeniedToast() {
      try {
        const raw = sessionStorage.getItem(ACCESS_DENIED_STORAGE_KEY);
        if (!raw) return;
        sessionStorage.removeItem(ACCESS_DENIED_STORAGE_KEY);
        const payload = JSON.parse(raw);
        const message = payload?.message || 'That page is outside your workspace.';
        if (window.$toast?.warning) {
          window.$toast.warning(message);
        }
      } catch {
        sessionStorage.removeItem(ACCESS_DENIED_STORAGE_KEY);
      }
    },
  },
}
</script>

<style>
.app {
  display: flex;
  min-height: 100vh;
}

.app-main-column {
  flex: 1;
  margin-left: 250px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  transition: margin-left 0.3s;
  background-color: var(--color-surface, #f8fafc);
}

.app-main-column.sidebar-collapsed {
  margin-left: 60px;
}

.main-content {
  flex: 1;
  margin-left: 0;
  padding: var(--spacing-lg);
  background-color: var(--color-surface);
  min-width: 0;
}

.main-content.no-sidebar {
  margin-left: 0;
  padding: 0;
  width: 100%;
}

.main-content.has-internal-footer {
  padding-bottom: 52px;
}

body:has(.sidebar.collapsed) .internal-footer {
  left: 60px !important;
}

html {
  scroll-padding-bottom: 52px;
}

body {
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  background: #f8f9fa;
}

* {
  box-sizing: border-box;
}

.page-title {
  margin-bottom: 2rem;
  color: #2c3e50;
}

.action-button {
  background-color: var(--color-primary);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.action-button:hover {
  background-color: var(--color-secondary);
}

.error-message {
  background-color: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 4px;
  margin: 1rem 0;
}

.main-nav {
  display: flex;
  align-items: center;
  padding-bottom: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
  border-bottom: 1px solid var(--color-border);
}

.main-nav a {
  margin-right: var(--spacing-md);
  font-weight: 600;
  color: var(--color-text-light);
  transition: color 0.2s;
}

.main-nav a:hover,
.main-nav a.router-link-exact-active {
  color: var(--color-primary);
}

.logout-button {
  margin-left: auto;
  background-color: var(--color-accent);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.logout-button:hover {
  background-color: #c49a1d;
}
</style>
